import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloError,
  AleloInput,
  AleloLayout,
} from '@/components/AleloLayout'
import { RangoRadioGroup } from '@/components/rango'
import { AleloCaptcha } from '@/components/AleloCaptcha'
import { AleloStepper, stepperTotal } from '@/components/AleloStepper'
import styles from '@/components/AleloLayout.module.css'
import {
  EMAIL_MAX_LENGTH,
  NAME_LIMITS,
  normalizeEmail,
  normalizePersonName,
  sanitizeEmailInput,
  toNameCase,
  validateEmail,
  validateMotherName,
  hasMotherNameFor,
  validatePersonName,
} from '@/utils/authValidation'
import {
  birthDateToIso,
  formatBirthDateInput,
  validateBirthDate,
} from '@/utils/birthDate'
import { transformInput } from '@/utils/inputTransform'
import { digitsOnly, formatCpf, isValidCpf } from '@/utils/cpf'
import { formatPhoneBr } from '@/utils/phone'
import { executeSafely } from '@/utils/helpers'
import * as loginCpfHint from '@/utils/loginCpfHint'
import * as signupDraft from '@/utils/signupDraft'
import * as signupFlowMarker from '@/utils/signupFlowMarker'
import * as verificationApi from '@/utils/signupVerificationApi'
import * as verificationToken from '@/utils/signupVerificationToken'
import type { CpfStatus, VerificationChannel } from '@/utils/signupVerificationApi'
import {
  describeOtpError,
  RESEND_SECONDS,
} from '@/screens/signup-shared/useSignupVerification'
import { useVerificationChannels } from '@/screens/signup-shared/useVerificationChannels'
import { useSignupIdManager } from './hooks/useSignupIdManager'
import { planPhoneSubmit } from './phoneStep'
import { VerifyStep } from './VerifyStep'
import locales from './locales/pt-BR.json'
import { errorCopy } from '@/utils/transactionError'

type Step = 'cpf' | 'form' | 'email' | 'phone'

/**
 * O indicador numerado (`AleloStepper`) só conta a partir de "Dados" — o
 * mesmo padrão que `login-id`→`login-password` já usa (duas telas, um passo
 * lógico só). A etapa `cpf` não aparece em `STEP_INDEX` de propósito: ela não
 * desenha `AleloStepper` nem o texto "Passo X de Y" (ver JSX abaixo).
 */
const STEP_INDEX: Record<Exclude<Step, 'cpf'>, number> = { form: 1, email: 2, phone: 3 }

type ProfileKey = 'givenName' | 'familyName' | 'dateOfBirth' | 'gender' | 'motherName'
type NameKey = 'givenName' | 'familyName' | 'motherName'

/** id do input de cada campo da etapa "Dados", na ordem visual: o foco vai para o primeiro com erro. */
const PROFILE_INPUT_IDS: Record<ProfileKey, string> = {
  givenName: 'given_name',
  familyName: 'family_name',
  dateOfBirth: 'date_of_birth',
  gender: 'gender',
  motherName: 'mother_name',
}

const PROFILE_KEYS = Object.keys(PROFILE_INPUT_IDS) as ProfileKey[]

const errorText = (code: string | null) =>
  code ? (locales.errors as Record<string, string>)[code] ?? locales.failed : undefined

const GENDER_OPTIONS = [
  { value: 'female', label: locales.genderFemale },
  { value: 'male', label: locales.genderMale },
  { value: 'non_binary', label: locales.genderNonBinary },
  { value: 'prefer_not_to_say', label: locales.genderPreferNotToSay },
]

/** Nome como será gravado: normalizado e com a inicial de cada palavra maiúscula. */
const cleanName = (value: string) => toNameCase(normalizePersonName(value))

function profileFieldError(key: ProfileKey, value: string): string | undefined {
  if (key === 'dateOfBirth') return errorText(validateBirthDate(value))
  if (key === 'motherName') return errorText(validateMotherName(value))
  if (key === 'gender') return value.trim() ? undefined : locales.invalidGender
  return errorText(validatePersonName(value, key))
}

/**
 * Identifier First — signup-id (CPF / e-mail / phone) antes da senha.
 *
 * Wizard local de 4 telas (CPF → Dados → E-mail → Celular). A checagem de
 * CPF existente roda sozinha na primeira, antes de qualquer outro campo — só
 * "Celular" chama o Auth0 de verdade (`handleContinue`, inalterado); as
 * demais só avançam o state local, sem tocar a rede.
 */
export default function SignupIdScreen() {
  const {
    handleContinue,
    handleBackToLogin,
    userExists,
    captchaConfig,
    captchaReady,
    onCaptchaToken,
    error: sdkError,
  } = useSignupIdManager()
  const [channels] = useVerificationChannels()

  // "Voltar" da tela de senha: o rascunho gravado no passo 3 volta com os
  // campos preenchidos e o wizard abre no celular. Sem marcador, vazio.
  const [restored] = useState(() => signupDraft.restore())
  // Recusa por CPF existente abre na etapa CPF, onde fica o aviso e o link
  // "Já tenho conta" já leva o CPF — não no celular, onde o rascunho levaria.
  const [step, setStep] = useState<Step>(restored && !userExists ? 'phone' : 'cpf')
  const [cpf, setCpf] = useState(restored?.cpf ?? '')
  const [email, setEmail] = useState(restored?.email ?? '')
  const [phone, setPhone] = useState(restored?.phone ?? '')
  const [givenName, setGivenName] = useState(restored?.givenName ?? '')
  const [familyName, setFamilyName] = useState(restored?.familyName ?? '')
  // O rascunho guarda a data em ISO; a tela mostra DD/MM/AAAA.
  const [dateOfBirth, setDateOfBirth] = useState(
    formatBirthDateInput(restored?.dateOfBirth ?? ''),
  )
  const [gender, setGender] = useState(restored?.gender ?? '')
  const [motherName, setMotherName] = useState(restored?.motherName ?? '')
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  // Erros da etapa "Dados" e do e-mail ficam no próprio campo (aria-invalid).
  const [profileErrors, setProfileErrors] = useState<
    Partial<Record<ProfileKey, string>>
  >({})
  const [emailError, setEmailError] = useState<string>()
  const [phoneError, setPhoneError] = useState<string>()

  // CPF já cadastrado (store Auth0 ou legado): avisa no passo 1, antes dos
  // OTPs. A consulta fica guardada por CPF (inclusive em andamento) para o
  // blur e o CONTINUAR não baterem no BFF duas vezes; `currentCpf` descarta
  // a resposta de um CPF que já foi editado.
  const [cpfExists, setCpfExists] = useState(false)
  // Rede de segurança: a Auth0 recusou o `signup()` por CPF existente. O erro
  // da transação não some sozinho, então editar o CPF o dispensa.
  const [userExistsDismissed, setUserExistsDismissed] = useState(false)
  const showCpfExists = cpfExists || (userExists && !userExistsDismissed)
  const cpfChecks = useRef(new Map<string, Promise<CpfStatus>>())
  const currentCpf = useRef(digitsOnly(cpf))

  // Verificação de contato: a sessão do BFF acumula a identidade, e cada canal
  // exigido passa por "código enviado" → "canal confirmado".
  const [sessionId, setSessionId] = useState('')
  const [sentFor, setSentFor] = useState<VerificationChannel[]>([])
  const [confirmedFor, setConfirmedFor] = useState<VerificationChannel[]>([])
  const [code, setCode] = useState('')
  const [resendSeconds, setResendSeconds] = useState(0)
  const [token, setToken] = useState('')

  useEffect(() => {
    if (resendSeconds <= 0) return
    const timer = window.setTimeout(() => setResendSeconds(resendSeconds - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [resendSeconds])

  const requires = (channel: VerificationChannel) =>
    (channels ?? []).includes(channel)
  const wasSent = (channel: VerificationChannel) => sentFor.includes(channel)
  const isConfirmed = (channel: VerificationChannel) =>
    confirmedFor.includes(channel)

  function goTo(next: Step) {
    setError(undefined)
    setCode('')
    setStep(next)
  }

  /**
   * O BFF prende a identidade à sessão no primeiro envio: corrigir o contato
   * recomeça a verificação do zero, em vez de bater em `invalid_session`.
   */
  function resetVerification() {
    setSessionId('')
    setSentFor([])
    setConfirmedFor([])
    setToken('')
    setCode('')
    setResendSeconds(0)
    setError(undefined)
  }

  /** Dispara (ou reenvia) o código do canal. */
  async function sendCode(channel: VerificationChannel) {
    setError(undefined)
    setPending(true)
    const result = await verificationApi.start({
      cpf: digitsOnly(cpf),
      ...(channel === 'email' ? { email: normalizeEmail(email) } : { phone }),
      channel,
      ...(sessionId ? { sessionId } : {}),
    })
    setPending(false)

    if (!result.ok) {
      setError(describeOtpError(result.error, locales.failed))
      return
    }
    setSessionId(result.sessionId)
    setSentFor((prev) => (prev.includes(channel) ? prev : [...prev, channel]))
    setCode('')
    setResendSeconds(RESEND_SECONDS)
  }

  /** Confere o código digitado e marca o canal como confirmado. */
  async function confirmCode(channel: VerificationChannel) {
    if (code.length !== 6) {
      setError(locales.invalidOtp)
      return
    }
    setError(undefined)
    setPending(true)
    const result = await verificationApi.confirm({ sessionId, channel, code })
    setPending(false)

    if (!result.ok) {
      setError(describeOtpError(result.error, locales.failed))
      return
    }
    if (result.verified) setToken(result.token)
    setConfirmedFor((prev) => (prev.includes(channel) ? prev : [...prev, channel]))
    setCode('')
  }

  function onCpfChange(value: string) {
    const next = formatCpf(value)
    setCpf(next)
    currentCpf.current = digitsOnly(next)
    setCpfExists(false)
    setUserExistsDismissed(true)
  }

  /** Consulta o BFF (fail-open: `unknown` deixa seguir) e mostra o aviso. */
  async function checkCpf(value: string): Promise<CpfStatus> {
    const digits = digitsOnly(value)
    let check = cpfChecks.current.get(digits)
    if (!check) {
      check = verificationApi.checkCpfStatus(digits)
      cpfChecks.current.set(digits, check)
    }
    const status = await check
    // `unknown` não fica guardado: a próxima tentativa pergunta de novo.
    if (status === 'unknown') cpfChecks.current.delete(digits)
    if (currentCpf.current === digits) setCpfExists(status === 'exists')
    return status
  }

  function onCpfBlur() {
    if (isValidCpf(cpf)) void checkCpf(cpf)
  }

  /** "Já tenho conta" com CPF existente: leva o CPF para o `login-id` já preenchido. */
  function goToLogin() {
    loginCpfHint.save(cpf)
    handleBackToLogin()
  }

  /** Step 1 — só avança localmente; nenhuma chamada ao Auth0 ainda. */
  /** Etapa CPF — sozinha, antes de qualquer outro campo (SCS-01/02/03). */
  async function onSubmitCpf(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    if (!isValidCpf(cpf)) {
      setError(locales.invalidCpf)
      return
    }
    setPending(true)
    const cpfStatus = await checkCpf(cpf)
    setPending(false)
    if (cpfStatus === 'exists') return
    goTo('form')
  }

  const profile: Record<ProfileKey, string> = {
    givenName,
    familyName,
    dateOfBirth,
    gender,
    motherName,
  }
  const setProfile: Record<ProfileKey, (value: string) => void> = {
    givenName: setGivenName,
    familyName: setFamilyName,
    dateOfBirth: setDateOfBirth,
    gender: setGender,
    motherName: setMotherName,
  }

  /** Campo com erro é revalidado a cada tecla: o erro some assim que o valor fica válido. */
  function changeProfile(key: ProfileKey, value: string) {
    setProfile[key](value)
    if (profileErrors[key]) {
      setProfileErrors((prev) => ({
        ...prev,
        [key]: profileFieldError(key, value),
      }))
    }
  }

  /** Ao sair: normaliza e valida. Campo vazio só acusa no envio, se ainda não acusava. */
  function blurName(key: NameKey) {
    const value = cleanName(profile[key])
    if (value !== profile[key]) setProfile[key](value)
    if (value || profileErrors[key]) {
      setProfileErrors((prev) => ({
        ...prev,
        [key]: profileFieldError(key, value),
      }))
    }
  }

  /** Data só acusa ao sair quando já tem algo digitado (vazio acusa no envio). */
  function blurDateOfBirth() {
    if (dateOfBirth || profileErrors.dateOfBirth) {
      setProfileErrors((prev) => ({
        ...prev,
        dateOfBirth: profileFieldError('dateOfBirth', dateOfBirth),
      }))
    }
  }

  /** Etapa "Dados" — o CPF já foi resolvido na etapa anterior. */
  function onSubmitProfile(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    const next = {
      ...profile,
      givenName: cleanName(givenName),
      familyName: cleanName(familyName),
      motherName: cleanName(motherName),
    }
    setGivenName(next.givenName)
    setFamilyName(next.familyName)
    setMotherName(next.motherName)
    const errors: Partial<Record<ProfileKey, string>> = {}
    for (const key of PROFILE_KEYS) {
      const message = profileFieldError(key, next[key])
      if (message) errors[key] = message
    }
    setProfileErrors(errors)
    const first = PROFILE_KEYS.find((key) => errors[key])
    if (first) {
      document.getElementById(PROFILE_INPUT_IDS[first])?.focus()
      return
    }
    goTo('email')
  }

  /**
   * "Cancelar" na etapa "Dados": o CPF fica travado ali (sem opção de
   * editar), então esta é a saída pra quem digitou o CPF errado — reinicia
   * o wizard na etapa CPF, limpando o CPF e os dados já preenchidos.
   */
  function onCancelProfile() {
    setCpf('')
    setGivenName('')
    setFamilyName('')
    setDateOfBirth('')
    setGender('')
    setMotherName('')
    setProfileErrors({})
    setCpfExists(false)
    setUserExistsDismissed(true)
    currentCpf.current = ''
    goTo('cpf')
  }

  /** Sem espaço nem maiúscula já enquanto se digita (SFU-03). */
  function onEmailChange(typed: string) {
    const value = sanitizeEmailInput(typed)
    setEmail(value)
    if (emailError) setEmailError(errorText(validateEmail(value)))
  }

  function onEmailBlur() {
    const value = normalizeEmail(email)
    if (value !== email) setEmail(value)
    if (value || emailError) setEmailError(errorText(validateEmail(value)))
  }

  /**
   * Step 2 — o mesmo botão avança pelos estados do passo: enviar o código,
   * conferir o código e, com o e-mail confirmado (ou sem o canal exigido),
   * seguir para o celular.
   */
  /**
   * O e-mail ou o celular já é de outra conta (spec `signup-contact-in-use`)?
   * Só `in_use` para o passo: `unknown` segue, e a Auth0 ainda recusa no fim.
   */
  async function contactInUse(contact: { email: string } | { phone: string }) {
    setPending(true)
    try {
      return (await verificationApi.checkContactStatus(contact)) === 'in_use'
    } finally {
      setPending(false)
    }
  }

  async function onSubmitEmail(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    const normalized = normalizeEmail(email)
    setEmail(normalized)
    const invalid = errorText(validateEmail(normalized))
    setEmailError(invalid)
    if (invalid) {
      document.getElementById('email')?.focus()
      return
    }
    // E-mail de outra conta para aqui, no campo (SCU-08). Já enviado ou
    // confirmado, ele passou por esta checagem antes.
    if (!wasSent('email') && !isConfirmed('email') && (await contactInUse({ email: normalized }))) {
      setEmailError(locales.emailInUse)
      document.getElementById('email')?.focus()
      return
    }
    if (!requires('email') || isConfirmed('email')) {
      goTo('phone')
      return
    }
    if (!wasSent('email')) {
      await sendCode('email')
      return
    }
    await confirmCode('email')
  }

  /**
   * Step 3 — confirma o celular pelo mesmo caminho do step 2 e, com tudo
   * verificado, fecha no BFF (que valida CPF, e-mail e celular e emite o
   * token) antes da única chamada real ao Auth0. A decisão está em
   * `planPhoneSubmit`; aqui só se executa.
   */
  async function onSubmitPhone(event: FormEvent) {
    event.preventDefault()
    setError(undefined)

    const action = planPhoneSubmit({
      phone,
      required: channels ?? [],
      sent: sentFor,
      confirmed: confirmedFor,
      token,
    })
    if (action === 'invalid_phone') {
      setError(locales.invalidPhone)
      return
    }
    // Celular de outra conta para aqui, no campo (SCU-09), em todo caminho
    // que ainda não passou por esta checagem: enviar o código, fechar sem OTP
    // ou seguir com o token do e-mail (`continue`, só o e-mail exigido).
    if (
      action !== 'confirm_code' &&
      !wasSent('sms') &&
      !isConfirmed('sms') &&
      (await contactInUse({ phone }))
    ) {
      setPhoneError(locales.phoneInUse)
      document.getElementById('phone')?.focus()
      return
    }
    if (action === 'send_code') {
      await sendCode('sms')
      return
    }
    if (action === 'confirm_code') {
      await confirmCode('sms')
      return
    }

    setPending(true)
    try {
      // Fechamento: o BFF só emite o token com a identidade completa e todos
      // os canais exigidos confirmados — o `confirm` do último canal já pode
      // tê-lo devolvido. Erro de identidade aparece aqui, não na tela de senha.
      let verified = token
      if (action === 'close') {
        const finished = await verificationApi.start({
          cpf: digitsOnly(cpf),
          email,
          phone,
          ...(sessionId ? { sessionId } : {}),
        })
        if (!finished.ok) {
          setError(describeOtpError(finished.error, locales.failed))
          return
        }
        if (!finished.verified) {
          setError(locales.failed)
          return
        }
        verified = finished.token
        setToken(finished.token)
      }
      // O token vale para o `signup()` da tela de senha, uma navegação adiante;
      // o rascunho, para o "Voltar" dela trazer os campos de volta.
      verificationToken.save(verified)
      signupDraft.save({
        cpf,
        email,
        phone,
        givenName,
        familyName,
        dateOfBirth: birthDateToIso(dateOfBirth),
        gender,
        motherName,
        hasMotherName: hasMotherNameFor(motherName),
      })
      // A passkey-enrollment, que a Auth0 abre antes da senha, lê o marcador
      // para pular a chave de acesso no cadastro (SMP-12/13).
      signupFlowMarker.start()
      await executeSafely(() =>
        handleContinue({
          username: cpf,
          email,
          phone,
          givenName,
          familyName,
          dateOfBirth: birthDateToIso(dateOfBirth),
          gender,
          hasMotherName: hasMotherNameFor(motherName),
          motherName: hasMotherNameFor(motherName) === 'yes' ? motherName : '',
        }),
      )
    } catch (err) {
      // Cadastro não seguiu: o marcador não pode pular a chave num login depois.
      signupFlowMarker.clear()
      if (err instanceof Error && err.message === 'CPF_INVALID') {
        setError(locales.invalidCpf)
      } else if (err instanceof Error && err.message === 'GIVEN_NAME_REQUIRED') {
        setError(locales.invalidGivenName)
      } else if (err instanceof Error && err.message === 'FAMILY_NAME_REQUIRED') {
        setError(locales.invalidFamilyName)
      } else if (err instanceof Error && err.message === 'EMAIL_INVALID') {
        setError(locales.invalidEmail)
      } else if (err instanceof Error && err.message === 'DATE_OF_BIRTH_REQUIRED') {
        setError(locales.invalidDateOfBirth)
      } else if (err instanceof Error && err.message === 'GENDER_REQUIRED') {
        setError(locales.invalidGender)
      } else if (err instanceof Error && err.message === 'PHONE_INVALID') {
        setError(locales.invalidPhone)
      } else if (err instanceof Error && err.message === 'CAPTCHA_REQUIRED') {
        setError(locales.captchaRequired)
      } else {
        // Demais códigos de campo (nome, e-mail) que o manager confere de novo.
        setError(errorText(err instanceof Error ? err.message : null) ?? locales.failed)
      }
    } finally {
      setPending(false)
    }
  }

  /** O botão do passo muda de papel: verificar → confirmar → avançar. */
  function stepLabel(channel: VerificationChannel): string {
    if (!requires(channel) || isConfirmed(channel)) return locales.submit
    return wasSent(channel) ? locales.confirmCode : locales.verify
  }

  // O wizard tem sempre três telas numeradas (Dados → E-mail → Celular),
  // porque a Auth0 exige e-mail e celular no cadastro — os canais só decidem
  // se cada uma pede código, não se ela existe. Por isso o indicador ignora a
  // config do BFF. A quarta etapa é `signup-password`, que fecha a jornada
  // criando a conta. A etapa CPF fica fora dessa contagem (ver `STEP_INDEX`).
  const totalSteps = stepperTotal(undefined, true)
  const cpfErrorId = 'signup-cpf-error'
  const cpfHasError = showCpfExists || Boolean(error)

  return (
    <AleloLayout wide={step === 'form'}>
      {step !== 'cpf' ? (
        <>
          <AleloStepper currentStep={STEP_INDEX[step]} withPassword />
          <p className={styles.signupHint}>{`Passo ${STEP_INDEX[step]} de ${totalSteps}`}</p>
        </>
      ) : null}

      {step === 'cpf' ? (
        <>
          <h1 className={styles.title}>{locales.cpfStepTitle}</h1>
          <p className={styles.subtitle}>{locales.cpfStepDescription}</p>
          <form className={styles.stack} onSubmit={onSubmitCpf} noValidate>
            <AleloInput
              id="cpf"
              label={locales.cpfLabel}
              name="username"
              inputMode="numeric"
              autoComplete="username"
              placeholder={locales.cpfPlaceholder}
              value={cpf}
              onChange={(e) => onCpfChange(e.target.value)}
              onBlur={onCpfBlur}
              maxLength={14}
              aria-invalid={cpfHasError || undefined}
              aria-describedby={cpfHasError ? cpfErrorId : undefined}
            />
            <AleloError
              id={cpfErrorId}
              message={showCpfExists ? locales.cpfAlreadyRegistered : error}
            />
            <AleloButton type="submit" disabled={pending}>
              {locales.submit}
            </AleloButton>
            <div className={styles.center}>
              {/* CPF já cadastrado: o mesmo link leva o CPF pro login. */}
              <AleloButton
                variant="link"
                onClick={showCpfExists ? goToLogin : handleBackToLogin}
              >
                {locales.backToLogin}
              </AleloButton>
            </div>
          </form>
        </>
      ) : null}

      {step === 'form' ? (
        <>
          <h1 className={styles.title}>{locales.title}</h1>
          <p className={styles.subtitle}>{locales.description}</p>
          <div className={styles.identifierRow}>
            <p className={styles.identifierValue}>{cpf}</p>
          </div>
          <form className={styles.stack} onSubmit={onSubmitProfile} noValidate>
            {/*
              Todos os campos ocupam a largura toda (`.fieldFull`): nomes podem
              ser longos e o gênero mostra as quatro opções de uma vez.
            */}
            <div className={styles.columns}>
              <div className={styles.fieldFull}>
                <AleloInput
                  id="given_name"
                  error={profileErrors.givenName}
                  label={locales.givenNameLabel}
                  name="given-name"
                  autoComplete="given-name"
                  autoCapitalize="words"
                  spellCheck={false}
                  maxLength={NAME_LIMITS.givenName}
                  placeholder={locales.givenNamePlaceholder}
                  value={givenName}
                  onChange={(e) =>
                    changeProfile('givenName', transformInput(e.target, toNameCase))
                  }
                  onBlur={() => blurName('givenName')}
                />
              </div>
              <div className={styles.fieldFull}>
                <AleloInput
                  id="family_name"
                  error={profileErrors.familyName}
                  label={locales.familyNameLabel}
                  name="family-name"
                  autoComplete="family-name"
                  autoCapitalize="words"
                  spellCheck={false}
                  maxLength={NAME_LIMITS.familyName}
                  placeholder={locales.familyNamePlaceholder}
                  value={familyName}
                  onChange={(e) =>
                    changeProfile('familyName', transformInput(e.target, toNameCase))
                  }
                  onBlur={() => blurName('familyName')}
                />
              </div>
              <div className={styles.fieldFull}>
                <AleloInput
                  id="date_of_birth"
                  error={profileErrors.dateOfBirth}
                  name="bday"
                  inputMode="numeric"
                  autoComplete="bday"
                  maxLength={10}
                  label={locales.dateOfBirthLabel}
                  placeholder={locales.dateOfBirthPlaceholder}
                  supportText={locales.dateOfBirthSupport}
                  value={dateOfBirth}
                  onChange={(e) =>
                    changeProfile(
                      'dateOfBirth',
                      transformInput(e.target, formatBirthDateInput),
                    )
                  }
                  onBlur={blurDateOfBirth}
                />
              </div>
              <div className={styles.fieldFull}>
                <RangoRadioGroup
                  id="gender"
                  name="sex"
                  legend={locales.genderLabel}
                  options={GENDER_OPTIONS}
                  value={gender}
                  error={profileErrors.gender}
                  onChange={(value) => changeProfile('gender', value)}
                />
              </div>
              <div className={styles.fieldFull}>
                <AleloInput
                  id="mother_name"
                  error={profileErrors.motherName}
                  name="ulp-mother_name"
                  autoCapitalize="words"
                  spellCheck={false}
                  maxLength={NAME_LIMITS.motherName}
                  label={locales.motherNameLabel}
                  placeholder={locales.motherNamePlaceholder}
                  supportText={locales.motherNameSupport}
                  value={motherName}
                  onChange={(e) =>
                    changeProfile('motherName', transformInput(e.target, toNameCase))
                  }
                  onBlur={() => blurName('motherName')}
                />
              </div>
            </div>
            <AleloError message={error} />
            <AleloButton type="submit">{locales.submit}</AleloButton>
            <div className={styles.center}>
              <AleloButton variant="link" onClick={onCancelProfile}>
                {locales.cancel}
              </AleloButton>
            </div>
          </form>
        </>
      ) : null}

      {step === 'email' ? (
        <VerifyStep
          title={locales.emailStepTitle}
          description={
            requires('email') ? locales.emailStepDescription : locales.emailStepCollect
          }
          field={{
            id: 'email',
            name: 'email',
            type: 'email',
            inputMode: 'email',
            autoComplete: 'email',
            label: locales.emailLabel,
            placeholder: locales.emailPlaceholder,
            value: email,
            onChange: onEmailChange,
            onBlur: onEmailBlur,
            maxLength: EMAIL_MAX_LENGTH,
          }}
          fieldError={emailError}
          code={
            wasSent('email') && !isConfirmed('email')
              ? { value: code, onChange: setCode }
              : undefined
          }
          onFix={wasSent('email') ? resetVerification : undefined}
          resend={
            wasSent('email') && !isConfirmed('email')
              ? { seconds: resendSeconds, onResend: () => void sendCode('email') }
              : undefined
          }
          confirmedHint={isConfirmed('email') ? locales.emailConfirmed : undefined}
          submitLabel={stepLabel('email')}
          error={error}
          pending={pending}
          onSubmit={(event) => void onSubmitEmail(event)}
          onBack={() => goTo('form')}
        />
      ) : null}

      {step === 'phone' ? (
        <VerifyStep
          title={locales.phoneStepTitle}
          description={
            requires('sms') ? locales.phoneStepDescription : locales.phoneStepCollect
          }
          field={{
            id: 'phone',
            name: 'phone',
            type: 'tel',
            inputMode: 'tel',
            autoComplete: 'tel',
            label: locales.phoneLabel,
            placeholder: locales.phonePlaceholder,
            value: phone,
            onChange: (value: string) => {
              setPhone(formatPhoneBr(value))
              setPhoneError(undefined)
            },
          }}
          fieldError={phoneError}
          code={
            wasSent('sms') && !isConfirmed('sms')
              ? { value: code, onChange: setCode }
              : undefined
          }
          onFix={wasSent('sms') ? resetVerification : undefined}
          resend={
            wasSent('sms') && !isConfirmed('sms')
              ? { seconds: resendSeconds, onResend: () => void sendCode('sms') }
              : undefined
          }
          confirmedHint={isConfirmed('sms') ? locales.phoneConfirmed : undefined}
          submitLabel={stepLabel('sms')}
          error={error || errorCopy(sdkError, locales.errors)}
          pending={pending || !captchaReady}
          onSubmit={(event) => void onSubmitPhone(event)}
          onBack={() => goTo('email')}
        >
          <AleloCaptcha config={captchaConfig} onToken={onCaptchaToken} />
        </VerifyStep>
      ) : null}
    </AleloLayout>
  )
}
